import { Stack, Typography } from "@mui/material";
import { SnowflakeIcon } from "../components/images";
import { BASE_API_URL } from "../api";
import { useStateWithSearchParams } from "../hooks";
import { CustomTabs } from "../components/ui/CustomTabs";
import { RequirementsRowItem } from "../types";
import {
  salesforceCompanyData,
  hubspotCompanyData,
  desktopCompanyData,
  salesforceContactData,
  desktopContactData,
  hubspotContactData,
} from "utils/requirementsPageData";

type Props = {
  column: string;
  description: string;
  example: string;
  withSnowflake?: boolean;
};

const Header = ({ column, description, example }: Props) => {
  return (
    <Stack
      direction="row"
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 2.2fr 2.2fr",
        backgroundColor: "#E1E6EF",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
      }}
    >
      {[column, description, example].map((el) => (
        <Typography
          key={el}
          padding="16px"
          variant="body1"
          color="#1F2932"
          fontWeight={500}
        >
          {el}
        </Typography>
      ))}
    </Stack>
  );
};

const Body = ({ column, description, example, withSnowflake }: Props) => {
  return (
    <Stack
      direction="row"
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 2.2fr 2.2fr",
        gridTemplateRows: "auto",
        borderBottom: "1px solid #E1E6EF",
        alignItems: "flex-start",
      }}
    >
      <Stack
        padding="16px"
        direction="row"
        gap="8px"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="body1" color="#1F2932">
          {column}
        </Typography>
        {withSnowflake && <SnowflakeIcon />}
      </Stack>

      <Typography padding="16px" variant="body1" color="#1F2932">
        {description}
      </Typography>
      <Typography padding="16px" variant="body1" color="#1F2932">
        {example}
      </Typography>
    </Stack>
  );
};

enum DataTypes {
  "desktop" = "desktop",
  "hubspot" = "hubspot",
  "salesforce" = "salesforce",
}

const tabList = [
  {
    label: "Desktop CSV",
    path: DataTypes.desktop,
  },
  {
    label: "Hubspot",
    path: DataTypes.hubspot,
  },
  {
    label: "Salesforce",
    path: DataTypes.salesforce,
  },
];

const mapper: Record<
  DataTypes,
  { company: RequirementsRowItem[]; contact: RequirementsRowItem[] }
> = {
  [DataTypes.desktop]: {
    company: desktopCompanyData,
    contact: desktopContactData,
  },
  [DataTypes.hubspot]: {
    company: hubspotCompanyData,
    contact: hubspotContactData,
  },
  [DataTypes.salesforce]: {
    company: salesforceCompanyData,
    contact: salesforceContactData,
  },
};

const RequirementsPage = () => {
  const { searchDict, setSearchValue } = useStateWithSearchParams<{
    tab: DataTypes;
  }>({ tab: DataTypes.desktop });

  const tabIndex = tabList.findIndex(({ path }) => path === searchDict.tab);

  const handleChange = (e: React.SyntheticEvent, newTab: number) => {
    setSearchValue("tab", tabList[newTab].path);
  };

  return (
    <Stack
      sx={{ px: { sm: 8, md: 8, lg: 10, xl: 20 }, pt: 4, pb: 6 }}
      gap="24px"
    >
      <Stack gap="12px">
        <Typography variant="h2" color="#1F2932" fontWeight={500}>
          Field Requirements for CSV Upload
        </Typography>
        <Typography variant="subtitle1" color="#4C545B">
          Refer to the tables to make sure that all imported values meet the
          requirements.
        </Typography>
      </Stack>
      <CustomTabs
        currentTab={tabIndex}
        onChange={handleChange}
        tabList={tabList.map(({ label }) => label)}
      />

      <Stack direction="row" gap="8px">
        <SnowflakeIcon />
        <Typography variant="body1" color="#1F2932">
          -
        </Typography>
        <Typography variant="body1" color="#1F2932">
          Required Column
        </Typography>
      </Stack>
      <Stack gap="12px">
        <Typography variant="h4" color="#1F2932" fontWeight={500}>
          Company Related Data
        </Typography>
        <Typography variant="subtitle1" color="#4C545B">
          Covers key company details like name, country, industry, geo location,
          and employee range for better segmentation and analysis.
        </Typography>
        {searchDict.tab === DataTypes.desktop && (
          <Typography variant="subtitle1" color="#4C545B">
            Use this{" "}
            <a
              href={`${BASE_API_URL}/examples/input-csv?file_type=company`}
              style={{ color: "#1C3C6C" }}
            >
              example CSV
            </a>{" "}
            as a guide when preparing your own data for upload company related
            data.
          </Typography>
        )}
      </Stack>
      <Stack>
        <Header column="Column" description="Description" example="Example" />
        {mapper[searchDict.tab].company.map(
          ({ column, description, example, required }) => (
            <Body
              key={column}
              withSnowflake={required}
              column={column}
              description={description}
              example={example}
            />
          ),
        )}
      </Stack>
      <Stack gap="12px" id="contact">
        <Typography variant="h4" color="#1F2932" fontWeight={500}>
          Contact Related Data
        </Typography>
        <Typography variant="subtitle1" color="#4C545B">
          Includes details about an employee, such as name, job title,
          department, seniority, function and information based on interactions
          like calls, emails and meetings, considering activity type, date,
          email direction, and call duration to measure engagement.
        </Typography>
        {searchDict.tab === DataTypes.desktop && (
          <Typography variant="subtitle1" color="#4C545B">
            Use this{" "}
            <a
              href={`${BASE_API_URL}/examples/input-csv?file_type=contact`}
              style={{ color: "#1C3C6C" }}
            >
              example CSV
            </a>{" "}
            as a guide when preparing your own data for upload contact related
            data.
          </Typography>
        )}
      </Stack>
      <Stack>
        <Header column="Column" description="Description" example="Example" />
        {mapper[searchDict.tab].contact.map(
          ({ column, description, example, required }) => (
            <Body
              key={column}
              withSnowflake={required}
              column={column}
              description={description}
              example={example}
            />
          ),
        )}
      </Stack>
    </Stack>
  );
};

export default RequirementsPage;
